import { Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import {
  computeSilverPrices,
  formatSilverMoney,
  silverCurrencyFor,
  SILVER_PREMIUM,
  type SilverCountry,
} from "@/lib/silver-data";
import { liveTimestamp, SITE_URL } from "@/lib/seo";
import { silverRatesQueryOptions, useSilverRates } from "@/lib/nonblocking-rates";
import { PremiumSilverCards } from "@/components/PremiumPriceCards";
import { DisplayAd } from "@/components/DisplayAd";
import { todayLocal, todayLocalLong, tzForCountry } from "@/lib/today-local";
import { TodayDateBlock } from "@/components/TodayDateBlock";
import { JumpLinks } from "@/components/JumpLinks";
import { getDateParts } from "@/lib/dateIntent";
import { PriceAlertBell } from "@/components/PriceAlertBell";
import { HubNextSteps } from "@/components/HubNextSteps";
import { FaqSection } from "@/components/FaqSection";
import { faqPageSchema } from "@/lib/seo";
import { silverCityData, fallbackFacts } from "@/lib/city-facts-clusters";
import { SilverChart } from "@/components/SilverChart";
import { RelatedSearches } from "@/components/RelatedSearches";

export const silverRatesQO = silverRatesQueryOptions;

type Props = {
  country: SilverCountry;
  cityName?: string;
  title: string;
  intro: string;
};

export function SilverPage({ country, cityName, title, intro }: Props) {
  const tz = tzForCountry(country);
  const dateShort = todayLocal(tz);
  const dateLong = todayLocalLong(tz);
  const regionLabel = cityName ?? (country === "pakistan" ? "Pakistan" : "India");
  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to={country === "pakistan" ? "/silver-rate-pakistan" : "/silver-rate-india"} className="hover:underline">
            Silver Rate {country === "pakistan" ? "Pakistan" : "India"}
          </Link>
          {cityName ? <> › {cityName}</> : null}
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">{title}</h1>
        <p className="mt-2 text-sm font-semibold text-primary" data-nosnippet>
          {dateLong} · live silver / chandi rate in {regionLabel}
        </p>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          <span className="font-medium text-foreground" data-nosnippet>As of {dateShort},</span>{" "}
          {intro}
        </p>
      </section>
      {/* 1. Chart on top (per user spec) */}
      <Suspense fallback={<div className="mx-auto max-w-5xl px-4 py-10 min-h-[360px]" />}>
        <SilverChartWired country={country} cityName={cityName} />
      </Suspense>
      {/* 2. Live silver rate + carat cards */}
      <Suspense fallback={<div className="mx-auto max-w-5xl px-4 py-10 min-h-[420px]">Loading live silver rate…</div>}>
        <SilverBoard country={country} cityName={cityName} />
      </Suspense>
      <section className="mx-auto max-w-5xl px-4 -mt-2 mb-4 min-h-[48px]">
        <PriceAlertBell country={country} metal="silver" currency={country === "pakistan" ? "PKR" : "INR"} />
      </section>
      {/* 3. Native-script CTA buttons */}
      <HubNextSteps variant={country === "pakistan" ? "silver-pk" : "silver-in"} />
      <SilverCityFactsAndFaqs country={country} cityName={cityName} />
      <div className="mx-auto max-w-5xl px-4">
        <div className="min-h-[280px]"><DisplayAd placement="mid-billboard" className="my-8" /></div>
      </div>
      <div className="mx-auto max-w-5xl px-4">
        <div className="min-h-[280px]"><DisplayAd placement="end-halfpage" className="my-8" /></div>
      </div>
      <Suspense fallback={null}>
        <SilverFooterFacts country={country} />
      </Suspense>
      <RelatedSearches
        title={`People also search — silver (chandi) rate ${country === "pakistan" ? "Pakistan" : "India"}`}
        links={country === "pakistan"
          ? [
              { label: "silver rate today pakistan", href: "/silver-rate-pakistan" },
              { label: "chandi ka rate per tola pakistan", href: "/silver-rate-pakistan" },
              { label: "chandi rate per 10 gram pakistan", href: "/silver-rate-pakistan" },
              { label: "chandi 1 kg rate pakistan", href: "/silver-rate-pakistan" },
              { label: "silver rate karachi today", href: "/silver-rate-karachi" },
              { label: "silver rate lahore today", href: "/silver-rate-lahore" },
              { label: "silver rate islamabad today", href: "/silver-rate-islamabad" },
              { label: "silver rate rawalpindi today", href: "/silver-rate-rawalpindi" },
              { label: "silver rate faisalabad today", href: "/silver-rate-faisalabad" },
              { label: "silver rate multan today", href: "/silver-rate-multan" },
              { label: "silver rate peshawar today", href: "/silver-rate-peshawar" },
              { label: "silver rate quetta today", href: "/silver-rate-quetta" },
              { label: "gold rate pakistan today", href: "/pakistan" },
              { label: "zakat nisab pakistan silver", href: "/pakistan/zakat-nisab" },
            ]
          : [
              { label: "silver rate today india", href: "/silver-rate-india" },
              { label: "chandi rate per 10 gram india", href: "/silver-rate-india" },
              { label: "silver rate per kg india", href: "/silver-rate-india" },
              { label: "silver rate mumbai today", href: "/silver-rate-mumbai" },
              { label: "silver rate delhi today", href: "/chandi-ka-rate-delhi" },
              { label: "silver rate chennai today", href: "/silver-rate-chennai" },
              { label: "silver rate kolkata today", href: "/silver-rate-kolkata" },
              { label: "silver rate bangalore today", href: "/silver-rate-bangalore" },
              { label: "silver rate hyderabad today", href: "/silver-rate-hyderabad-india" },
              { label: "silver rate ahmedabad today", href: "/silver-rate-ahmedabad" },
              { label: "silver rate jaipur today", href: "/chandi-ka-rate-jaipur" },
              { label: "silver rate lucknow today", href: "/chandi-ka-rate-lucknow" },
              { label: "gold rate today india", href: "/india" },
              { label: "zakat calculator", href: "/zakat-calculator" },
            ]
        }
      />
    </Layout>
  );
}

function SilverChartWired({ country, cityName }: { country: SilverCountry; cityName?: string }) {
  const { data } = useSilverRates();
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeSilverPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country });
  const anchor = country === "pakistan" ? prices.perTola : prices.per10g;
  return <SilverChart country={country} currentPrice={anchor} cityKey={cityName ?? "hub"} />;
}

function SilverFooterFacts({ country }: { country: SilverCountry }) {
  const { data } = useSilverRates();
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeSilverPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country });
  const currency = silverCurrencyFor(country);

  return (
    <SilverDateIntent
      country={country}
      perTola={formatSilverMoney(prices.perTola, currency)}
      per10g={formatSilverMoney(prices.per10g, currency)}
      perGram={formatSilverMoney(prices.perGram, currency)}
    />
  );
}

/**
 * Injected below SilverBoard on every SilverPage instance (hub or city).
 * When cityName is provided, uses bespoke silverCityData if available;
 * hub instances (no cityName) get country-wide generic facts + a
 * silver-hub FAQ set.
 */
function SilverCityFactsAndFaqs({ country, cityName }: { country: SilverCountry; cityName?: string }) {
  const isPk = country === "pakistan";
  const currencyLabel = isPk ? "PKR" : "₹";
  const unitLabel = isPk ? "per tola" : "per 10 gram / per kg";
  const label = cityName ?? (isPk ? "Pakistan" : "India");
  const slug = cityName?.toLowerCase().replace(/\s+/g, "-") ?? "";
  const cluster = cityName ? silverCityData(isPk ? "pakistan" : "india", slug) : null;
  const facts =
    cluster?.facts ?? (cityName
      ? fallbackFacts(cityName, isPk ? "pakistan" : "india", "silver")
      : [
          { label: "Metal", value: "999 fine silver + 925 sterling" },
          { label: "Refresh cadence", value: "Every 10 minutes" },
          { label: "Base spot", value: "International XAG/USD" },
          { label: "Popular unit", value: unitLabel },
        ]);
  const bespokeFaqs = cluster?.faqs ?? [];
  const hubFaqs = [
    {
      q: `${label} mein aaj chandi (silver) ka rate kya hai ${unitLabel}?`,
      a: `${label} ka live silver rate ${unitLabel} iss page ke rate table mein har 10 minute par refresh hota hai. International XAG spot + USD/${currencyLabel === "PKR" ? "PKR" : "INR"} + fixed local premium ke base pe calculate hota hai.`,
    },
    {
      q: `Silver ka rate ${label} mein kaise decide hota hai?`,
      a: `Formula: (XAG spot USD/oz ÷ 31.1035) × USD/${currencyLabel === "PKR" ? "PKR" : "INR"} + local dealer premium. ${isPk ? "Sarafa Bazaar Association" : "IBJA-style bullion desks"} isi base ke qareeb roz announce karti hain. Making charge sirf jewellery par apply hoti hai — coin/bar par nahi.`,
    },
    {
      q: `Silver zakat ka nisab ${label} mein kya hai?`,
      a: `Silver nisab = 52.5 tola (~612.36 gram) chandi ke barabar wealth. Agar total wealth (silver + gold + cash + business) is threshold se upar ho aur ek Hijri saal purani ho, 2.5% zakat lagti hai. Live nisab value calculate karne ke liye ${isPk ? "/pakistan/zakat-nisab" : "/zakat-calculator"} pe jayein.`,
    },
  ];
  const combined = [...bespokeFaqs, ...hubFaqs];
  const related = cluster?.related ?? [
    { href: isPk ? "/pakistan" : "/india", label: `Gold rate ${isPk ? "Pakistan" : "India"}` },
    { href: isPk ? "/silver-rate-pakistan" : "/silver-rate-india", label: `Silver rate ${isPk ? "Pakistan" : "India"} — all cities` },
    { href: isPk ? "/pakistan/calculator" : "/gold-purity-calculator", label: isPk ? "Pakistan calculator" : "Purity calculator" },
    { href: isPk ? "/pakistan/zakat-nisab" : "/zakat-calculator", label: "Zakat calculator" },
  ];

  return (
    <>
      <section className="mx-auto max-w-5xl px-4 py-4" aria-label={`${label} silver quick facts`}>
        <h2 className="text-lg font-bold mb-3">{label} silver market — quick facts</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {facts.map((f) => (
            <div key={f.label} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="font-semibold text-right">{f.value}</span>
            </div>
          ))}
        </div>
      </section>
      <div className="mx-auto max-w-5xl px-4">
        <FaqSection faqs={combined} title={`${label} silver / chandi rate — FAQs`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema(combined)) }}
        />
      </div>
      <section className="mx-auto max-w-5xl px-4 py-4" aria-label={`Related pages — ${label} silver`}>
        <h2 className="text-base font-bold mb-3">Related on DrHint</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {related.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:border-primary hover:bg-primary/5 transition-colors"
            >
              {l.label} →
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

function SilverBoard({ country, cityName }: { country: SilverCountry; cityName?: string }) {
  const { data } = useSilverRates();
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeSilverPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country });
  const currency = silverCurrencyFor(country);
  const tz = country === "pakistan" ? "PKT" : "IST";
  const updated = liveTimestamp(data.fetchedAt, tz);
  const unit: "tola" | "10g" = country === "pakistan" ? "tola" : "10g";
  const headlinePrice = unit === "tola" ? prices.perTola : prices.per10g;
  const headlineLabel = unit === "tola" ? "Per Tola (11.66 g)" : "Per 10 Grams";
  const regionName = cityName ?? (country === "pakistan" ? "Pakistan" : "India");
  const pageUrl = `${SITE_URL}${typeof window !== "undefined" ? window.location.pathname : ""}`;

  const dataset = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `Live Silver (Chandi) Rate — ${regionName}`,
    description: `Hourly-refreshed live silver / chandi rate in ${regionName}, calculated from international XAG spot price and live USD/${currency} exchange rate.`,
    keywords: ["silver rate", "chandi rate", regionName, "silver price today", "1 tola silver", "10 gram silver"],
    creator: { "@type": "Organization", name: "DrHint", url: SITE_URL },
    license: SITE_URL,
    temporalCoverage: data.fetchedAt,
    variableMeasured: [
      { "@type": "PropertyValue", name: "Silver per tola", value: Math.round(prices.perTola), unitText: currency },
      { "@type": "PropertyValue", name: "Silver per 10 grams", value: Math.round(prices.per10g), unitText: currency },
      { "@type": "PropertyValue", name: "Silver per gram", value: Math.round(prices.perGram), unitText: currency },
      { "@type": "PropertyValue", name: "Silver per kilogram", value: Math.round(prices.perKg), unitText: currency },
    ],
  };
  const product = {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    name: `Silver Rate ${regionName} (per ${unit === "tola" ? "tola" : "10g"})`,
    description: `Live silver / chandi rate in ${regionName} updated every 10 minutes.`,
    provider: { "@type": "Organization", name: "DrHint", url: SITE_URL },
    feesAndCommissionsSpecification: `Includes fixed local premium of ${currency} ${SILVER_PREMIUM}.`,
    offers: {
      "@type": "Offer",
      price: Math.round(headlinePrice),
      priceCurrency: currency,
      url: pageUrl,
      availability: "https://schema.org/InStock",
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: country === "pakistan" ? "PK" : "IN",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: "0", currency },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: country === "pakistan" ? "PK" : "IN",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
        },
      },
    },
  };
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is the live silver rate in ${regionName} today?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Today's live silver rate in ${regionName} is ${formatSilverMoney(prices.perTola, currency)} per tola (${formatSilverMoney(prices.per10g, currency)} per 10g, ${formatSilverMoney(prices.perGram, currency)} per gram). Updated ${updated}.`,
        },
      },
      {
        "@type": "Question",
        name: "How is the silver / chandi rate calculated?",
        acceptedAnswer: {
          "@type": "Answer",
          text: `International XAG spot price (USD/oz) ÷ 2.67 × today's USD/${currency} rate + ${currency} ${SILVER_PREMIUM} fixed local premium. Refreshed every 10 minutes.`,
        },
      },
      {
        "@type": "Question",
        name: "Does the rate update automatically?",
        acceptedAnswer: { "@type": "Answer", text: "Yes — every 10 minutes, synced with the gold rate engine." },
      },
    ],
  };

  return (
    <section className="mx-auto max-w-5xl px-4 pb-10">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(dataset) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(product) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faq) }} />

      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-6 md:p-8 shadow-lg">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          Live · Silver (Chandi) · {regionName}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">Today Silver Rate in {regionName} — {headlineLabel}</div>
        <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight leading-none text-foreground">
          {formatSilverMoney(headlinePrice, currency)}
        </div>
        <div className="mt-3 text-sm text-foreground/80">
          <span className="font-semibold">Per 10 Grams:</span>{" "}
          <span className="font-bold">{formatSilverMoney(prices.per10g, currency)}</span>
          <span className="mx-2 text-muted-foreground">·</span>
          <span className="font-semibold">Per Gram:</span>{" "}
          <span className="font-bold">{formatSilverMoney(prices.perGram, currency)}</span>
        </div>
        <div className="mt-3 text-xs text-muted-foreground" data-nosnippet>
          Updated <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
        </div>
      </div>

      <div className="mt-4 text-xs text-muted-foreground" data-nosnippet>
        Spot: ${data.spotUsdPerOz.toFixed(2)}/oz · 1 USD = {fx.toFixed(2)} {currency} · + {currency} {SILVER_PREMIUM} local premium · {updated}
      </div>

      <PremiumSilverCards
        prices={prices}
        currency={currency}
        regionLabel={regionName}
        formatter={formatSilverMoney}
      />

      <DisplayAd placement="inline-2" className="my-8" />

    </section>
  );
}

function SilverDateIntent({
  country,
  perTola,
  per10g,
  perGram,
}: {
  country: SilverCountry;
  perTola: string;
  per10g: string;
  perGram: string;
}) {
  const date = getDateParts(country);
  return (
    <>
      <TodayDateBlock
        country={country}
        date={date}
        metalLabel="Silver"
        tiles={[
          { label: "Per Tola", value: perTola },
          { label: "Per 10 g", value: per10g },
          { label: "Per Gram", value: perGram },
        ]}
      />
      <JumpLinks country={country} date={date} />
    </>
  );
}